import React from 'react';
import {
  Box, Typography, Paper, Stepper, Step, StepLabel, Button, Alert,
  CircularProgress, Stack, Divider, TextField, List, ListItem, ListItemIcon,
  ListItemText, Chip,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import Colors from '../../components/ui/Colors';
import { getOnboardingStatus } from '../../utilityFunctions/OnboardingUtil';
import { previewTableInjection, commitTableInjection, getCommitJobStatus } from '../../utilityFunctions/AdminUtil';

const STEPS = ['Your workspace', 'Provisioning', 'Upload your first data', 'Done'];
const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;
const POLL_MS = 2500;

export default function SelfServeOnboardingWizard(props) {
  const { loginUser } = props;
  const jwtToken = loginUser && loginUser.jwtToken;

  const [activeStep, setActiveStep] = React.useState(0);
  const [status, setStatus] = React.useState(null);

  // --- Provisioning poll (step 2) -----------------------------------------
  React.useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      const res = await getOnboardingStatus(jwtToken);
      if (cancelled) return;
      if (res.ok) {
        setStatus(res.data);
        const js = res.data && res.data.jobStatus;
        if (js === 'ACTIVE' || js === 'FAILED') return; // stop polling — terminal
      }
      timer = setTimeout(tick, POLL_MS);
    };

    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [jwtToken]);

  const jobStatus = status && status.jobStatus;
  const provisioned = jobStatus === 'ACTIVE';
  const failed = jobStatus === 'FAILED';

  // --- Upload flow (step 3), reusing the table-injection util calls --------
  const [file, setFile] = React.useState(null);
  const [tableName, setTableName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [tableNameError, setTableNameError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');
  const [uploaded, setUploaded] = React.useState(false);

  const validateTableName = (name) => {
    if (!name) return 'Required';
    if (name.length > 64) return 'Must be 64 characters or fewer';
    if (!SNAKE_CASE_RE.test(name)) return 'Use snake_case (lowercase letters, digits, underscores; must start with a letter)';
    return '';
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollCommitJob = async (jobId) => {
    const deadline = Date.now() + 30 * 60 * 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await sleep(3000);
      const res = await getCommitJobStatus(jobId, jwtToken);
      if (res.ok) {
        const job = res.data;
        if (job.status === 'SUCCEEDED' || job.status === 'FAILED') return job;
      }
      if (Date.now() > deadline) {
        return { status: 'FAILED', error: 'Commit is still running — check back later or your email.' };
      }
    }
  };

  const handleUpload = async () => {
    const nameErr = validateTableName(tableName);
    setTableNameError(nameErr);
    if (nameErr) return;
    if (!file) { setUploadError('Choose a CSV or XLSX file first.'); return; }

    setUploadError('');
    setBusy(true);

    // 1) Preview (schema inference + LLM metadata) — reused as-is from AdminUtil.
    const preview = await previewTableInjection(file, tableName, description, jwtToken);
    if (!preview.ok) {
      setBusy(false);
      setUploadError(preview.error || 'Preview failed.');
      return;
    }

    // 2) Commit the previewed staging directly (accept the inferred metadata).
    const commit = await commitTableInjection({ stagingId: preview.data.stagingId }, jwtToken);
    if (!commit.ok) {
      setBusy(false);
      setUploadError(commit.error || 'Commit failed.');
      return;
    }

    // 3) Poll the async commit job to completion.
    const job = await pollCommitJob(commit.data.jobId);
    setBusy(false);
    if (job.status !== 'SUCCEEDED') {
      setUploadError(job.error || 'Commit failed.');
      return;
    }
    setUploaded(true);
    setActiveStep(3);
  };

  // --- Step bodies ---------------------------------------------------------
  const renderWorkspaceStep = () => {
    if (!status) {
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Setting up your workspace…</Typography>
        </Stack>
      );
    }
    const rows = [
      ['Organization', loginUser && (loginUser.tenantName || loginUser.organization)],
      ['Workspace slug', status.slug],
      ['Catalog', status.catalog],
    ];
    return (
      <Stack spacing={1.5} sx={{ py: 1 }}>
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography color="text.secondary">{label}</Typography>
            <Typography sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
          </Box>
        ))}
      </Stack>
    );
  };

  const renderProvisioningStep = () => {
    const completed = (status && Array.isArray(status.completedSteps)) ? status.completedSteps : [];
    return (
      <Box sx={{ py: 1 }}>
        {failed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(status && status.error) || 'Provisioning failed. Please contact support.'}
          </Alert>
        )}
        <List dense>
          {completed.map((step) => (
            <ListItem key={step} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleRoundedIcon sx={{ color: Colors.secondary }} />
              </ListItemIcon>
              <ListItemText primary={step} />
            </ListItem>
          ))}
          {!provisioned && !failed && (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CircularProgress size={20} />
              </ListItemIcon>
              <ListItemText
                primary={jobStatus ? `Working… (${jobStatus})` : 'Starting…'}
                primaryTypographyProps={{ color: 'text.secondary' }}
              />
            </ListItem>
          )}
          {completed.length === 0 && (provisioned || failed) && (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <RadioButtonUncheckedRoundedIcon color="disabled" />
              </ListItemIcon>
              <ListItemText primary="No steps reported." primaryTypographyProps={{ color: 'text.secondary' }} />
            </ListItem>
          )}
        </List>
        {provisioned && (
          <Alert severity="success" sx={{ mt: 1 }}>Your workspace is active.</Alert>
        )}
      </Box>
    );
  };

  const renderUploadStep = () => (
    <Stack spacing={2.5} sx={{ py: 1 }}>
      <Typography color="text.secondary">
        Upload a CSV or XLSX to seed your first table. The system infers the schema and enriches the
        metadata automatically — the agentic pipeline can query it right after.
      </Typography>

      {uploadError && <Alert severity="error">{uploadError}</Alert>}

      <Button
        component="label"
        variant="outlined"
        startIcon={<UploadFileIcon />}
        disabled={busy}
        sx={{ alignSelf: 'flex-start', borderColor: Colors.primary, color: Colors.primary }}
      >
        {file ? 'Choose a different file' : 'Choose CSV or XLSX'}
        <input
          type="file"
          hidden
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)}
          disabled={busy}
        />
      </Button>
      {file && <Chip label={file.name} onDelete={busy ? undefined : () => setFile(null)} sx={{ alignSelf: 'flex-start' }} />}

      <TextField
        label="Table name (snake_case)"
        value={tableName}
        onChange={(e) => { setTableName(e.target.value); setTableNameError(validateTableName(e.target.value)); }}
        error={!!tableNameError}
        helperText={tableNameError || 'lowercase, digits, underscores; ≤ 64 chars'}
        inputProps={{ maxLength: 64 }}
        disabled={busy}
        sx={{ maxWidth: 420 }}
      />
      <TextField
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        minRows={2}
        disabled={busy}
        sx={{ maxWidth: 420 }}
      />

      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={busy || !file || !tableName || !!tableNameError}
        startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ alignSelf: 'flex-start', backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
      >
        {busy ? 'Uploading…' : 'Upload table'}
      </Button>
    </Stack>
  );

  const renderDoneStep = () => (
    <Stack spacing={2} alignItems="center" sx={{ py: 3, textAlign: 'center' }}>
      <RocketLaunchRoundedIcon sx={{ fontSize: 56, color: Colors.secondary }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color: Colors.primary }}>You&apos;re all set!</Typography>
      <Typography color="text.secondary">
        {uploaded
          ? 'Your workspace is active and your first table is live.'
          : 'Your workspace is active and ready to go.'}
      </Typography>
      <Button
        variant="contained"
        startIcon={<RocketLaunchRoundedIcon />}
        onClick={() => { window.location.href = '/data_analytics/home'; }}
        sx={{ backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
      >
        Go to Data Analytics
      </Button>
    </Stack>
  );

  const stepBody = [renderWorkspaceStep, renderProvisioningStep, renderUploadStep, renderDoneStep][activeStep]();

  // Gate the Next button per step.
  const canAdvance = () => {
    if (activeStep === 0) return true;
    if (activeStep === 1) return provisioned;
    if (activeStep === 2) return uploaded;
    return false;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 780, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
      <Typography variant="h5" sx={{ color: Colors.primary, fontWeight: 700, mb: 0.5 }}>
        Welcome — let&apos;s set up your workspace
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A few quick steps and you&apos;ll be ready to explore your data.
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 2 }} />

        {stepBody}

        {activeStep < 3 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                disabled={activeStep === 0 || busy}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={!canAdvance() || busy}
                sx={{ backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
              >
                Next
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
